/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * workspaceSlice.ts — workspac\1 \2lice (services/operator-web-app).
 */

/** Provenance for scenario fields — plan/arm-operator/05 */
export type FieldSource = 'manual' | 'ai_apply' | 'integration';

export interface ScenarioFieldValue {
  value: string;
  source: FieldSource;
}

/** L1 card: identity and segment (plan 05) */
export interface CustomerCardL1 {
  name: string;
  company: string;
  phone: string;
  clientId: string;
  segment: string;
  scenario: string;
}

export interface CustomerCard extends CustomerCardL1 {
  scenarioFields: Record<string, ScenarioFieldValue>;
}

/** Outcome of the call for wrap-up (Screen 07). */
export type WrapUpOutcome = 'meeting_scheduled' | 'callback' | 'resolved' | 'no_answer' | 'other';

export interface WorkspaceState {
  customerId: string | null;
  loadedAt: string | null;
  customerCard: CustomerCard | null;
  scenarioFields: Record<string, ScenarioFieldValue>;
  meetingConflict: { code: string; message: string; alternatives?: string[] } | null;
  /** E5-S1: Set when operator clicks "Подтвердить встречу" (draft -> confirmed). */
  meetingConfirmedAt: string | null;
  addressWarning: { code: string; message: string } | null;
  /** E5-S2: Coordinates for selected address (for map pin). */
  addressCoords: { lat: number; lon: number } | null;
  wrapUpRequiredFields: string[];
  /** Selected outcome when in wrap-up (post-call). */
  wrapUpOutcome: WrapUpOutcome | null;
  lastEventId: string | null;
}

/** Result of required-fields completeness check (E4-S3 policy engine). */
export interface WrapUpCompleteness {
  complete: boolean;
  missing: string[];
  filled: string[];
}

export const initialWorkspaceState: WorkspaceState = {
  customerId: null,
  loadedAt: null,
  customerCard: null,
  scenarioFields: {},
  meetingConflict: null,
  meetingConfirmedAt: null,
  addressWarning: null,
  addressCoords: null,
  wrapUpRequiredFields: [],
  wrapUpOutcome: null,
  lastEventId: null,
};

/**
 * E4-S3: Required fields policy engine.
 * Returns completeness of wrap-up: all required fields must have non-empty (trimmed) value.
 */
export function getWrapUpCompleteness(state: WorkspaceState): WrapUpCompleteness {
  const required = state.wrapUpRequiredFields;
  const fields = state.scenarioFields;
  const missing: string[] = [];
  const filled: string[] = [];
  for (const fieldId of required) {
    const v = fields[fieldId]?.value?.trim() ?? '';
    if (v === '') {
      missing.push(fieldId);
    } else {
      filled.push(fieldId);
    }
  }
  return {
    complete: missing.length === 0,
    missing,
    filled,
  };
}

export function reduceWorkspace(
  state: WorkspaceState,
  eventType: string,
  payload: Record<string, unknown>,
  eventId: string
): WorkspaceState {
  switch (eventType) {
    case 'customer_context_loaded': {
      const p = payload as { customer_id: string; loaded_at: string };
      return {
        ...state,
        customerId: p.customer_id,
        loadedAt: p.loaded_at,
        lastEventId: eventId,
      };
    }
    case 'meeting_conflict': {
      const p = payload as { conflict_code: string; message: string; alternatives?: string[] };
      return {
        ...state,
        meetingConflict: {
          code: p.conflict_code,
          message: p.message,
          alternatives: p.alternatives,
        },
        meetingConfirmedAt: null,
        lastEventId: eventId,
      };
    }
    case 'address_validation_warning': {
      const p = payload as { code: string; message: string };
      return {
        ...state,
        addressWarning: { code: p.code, message: p.message },
        lastEventId: eventId,
      };
    }
    case 'wrap_up_required': {
      const p = payload as { required_fields: string[] };
      return {
        ...state,
        wrapUpRequiredFields: p.required_fields ?? [],
        lastEventId: eventId,
      };
    }
    default:
      return state;
  }
}
